import {
  ArrowUpRight,
  CalendarCheck2,
  ChevronDown,
  Clock3,
  Globe2,
  HeartHandshake,
  Mail,
  MapPin,
  Phone,
  ShieldCheck,
} from 'lucide-react'
import Link from 'next/link'
import { venueDirectionsUrl, venueMapEmbedUrl } from '@/lib/venue-map'
import type { PublicVenue } from './domain'

const weekdayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

const safetyLabels: Record<string, string> = {
  proof_of_vaccination: 'Proof of vaccination required',
  contact_tracing: 'Contact tracing information collected',
  guest_temperature_check: 'Guest temperature checks',
  staff_temperature_check: 'Staff temperature checks',
  sick_staff_prohibited: 'Staff with symptoms prohibited from working',
  guest_masks: 'Masks required for guests',
  guest_masks_provided: 'Masks provided for guests',
  staff_masks: 'Masks required for staff',
  staff_gloves: 'Gloves required for staff',
  fully_vaccinated_staff: 'Fully vaccinated staff',
  limited_guests: 'Limited number of guests',
  common_area_distancing: 'Distancing enforced in common areas',
  extra_table_spacing: 'Extra space between tables',
  designated_pickup_area: 'Designated pickup area',
  cash_not_accepted: 'Cashless payments only',
  guest_sanitizer: 'Hand sanitizer available to guests',
  surfaces_between_seatings: 'Surfaces sanitized between seatings',
  air_filtration: 'Enhanced air filtration',
  daily_deep_clean: 'Daily deep cleaning',
  back_of_house_cleaning: 'Enhanced back-of-house cleaning',
  cleaning_logs: 'Cleaning logs maintained',
  safe_menus: 'Single-use or digital menus',
  contactless_payment: 'Contactless payment available',
  staff_safety_training: 'Staff safety training',
  added_food_safety_protocols: 'Additional food safety protocols',
}

export function PublicVenueDetails({ venue }: { venue: PublicVenue }) {
  const hours = groupHours(venue.hours ?? [])
  const address = venue.address
  const coordinates = {
    latitude: venue.latitude,
    longitude: venue.longitude,
  }
  const mapUrl = address ? venueMapEmbedUrl(coordinates) : ''
  const directionsUrl = address ? venueDirectionsUrl(coordinates) : ''
  const contact = venue.contact
  const links = venue.links
  const faqs = venue.faqs ?? []
  const safetyMeasures = venue.safety?.display
    ? venue.safety.measures.flatMap((measure) => safetyLabels[measure] ? [safetyLabels[measure]] : [])
    : []
  const hasVisitDetails = Boolean(address || hours.length || contact?.email || contact?.phone)
  const hasLinks = Boolean(
    links?.websiteUrl
      || links?.menuUrl
      || links?.facebookUsername
      || links?.instagramUsername
      || links?.xUsername,
  )

  if (!venue.description && !hasVisitDetails && !hasLinks && !faqs.length && !safetyMeasures.length) return null

  return (
    <section className="public-venue-details" id="venue-details">
      <header>
        <h3>Details</h3>
      </header>

      <div className="public-venue-detail-content">
        {venue.description ? (
          <section className="public-detail-about">
            <span><HeartHandshake /> About {venue.publicName}</span>
            <p>{venue.description}</p>
          </section>
        ) : null}

        {hasVisitDetails ? (
          <section className="public-detail-visit">
            <h4>Hours and location</h4>
            <div className="public-visit-grid">
              {address ? (
                <>
                  <div>
                    <MapPin />
                    <span>
                      <strong>{address.addressLine1}</strong>
                      {address.addressLine2 ? <small>{address.addressLine2}</small> : null}
                      <small>{formatLocality(address.city, address.region, address.postalCode)}</small>
                      {address.parkingOptions.length ? <em>{formatParking(address.parkingOptions)}</em> : null}
                      {directionsUrl ? (
                        <a href={directionsUrl} target="_blank" rel="noreferrer">
                          Get directions <ArrowUpRight />
                        </a>
                      ) : null}
                    </span>
                  </div>
                  {mapUrl ? (
                    <div className="public-venue-map">
                      <iframe
                        title={`Map of ${venue.publicName}`}
                        src={mapUrl}
                        loading="lazy"
                        referrerPolicy="no-referrer"
                      />
                    </div>
                  ) : null}
                </>
              ) : null}
              {hours.length ? (
                <div>
                  <Clock3 />
                  <span className="public-hours-list">
                    {hours.map((item) => (
                      <span key={item.day}>
                        <strong>{item.day}</strong>
                        <small>{item.windows.join(' · ')}</small>
                      </span>
                    ))}
                  </span>
                </div>
              ) : null}
              {contact?.email || contact?.phone ? (
                <div>
                  <CalendarCheck2 />
                  <span>
                    {contact.phone ? <a href={`tel:${contact.phone}`}><Phone /> {formatPhone(contact.phone)}</a> : null}
                    {contact.email ? <a href={`mailto:${contact.email}`}><Mail /> {contact.email}</a> : null}
                    {contact.acceptsWalkIns ? <em>Walk-ins welcome when space allows.</em> : null}
                  </span>
                </div>
              ) : null}
            </div>
          </section>
        ) : null}

        {safetyMeasures.length ? (
          <section className="public-detail-safety">
            <span><ShieldCheck /> Safety measures</span>
            <ul>{safetyMeasures.map((measure) => <li key={measure}>{measure}</li>)}</ul>
          </section>
        ) : null}

        {faqs.length ? (
          <section className="public-detail-faqs">
            <h4>Frequently asked questions</h4>
            <div>
              {faqs.map((faq) => (
                <details key={faq.id}>
                  <summary>{faq.question}<ChevronDown /></summary>
                  <p>{faq.answer}</p>
                </details>
              ))}
            </div>
          </section>
        ) : null}

        {hasLinks || venue.privateEventsEnabled ? (
          <nav className="public-detail-links" aria-label={`${venue.publicName} links`}>
            {links?.websiteUrl ? <a href={links.websiteUrl} target="_blank" rel="noreferrer"><Globe2 /> Website <ArrowUpRight /></a> : null}
            {links?.menuUrl ? <a href={links.menuUrl} target="_blank" rel="noreferrer">Menu <ArrowUpRight /></a> : null}
            {links?.instagramUsername ? <a href={`https://instagram.com/${encodeURIComponent(links.instagramUsername)}`} target="_blank" rel="noreferrer">Instagram <ArrowUpRight /></a> : null}
            {links?.facebookUsername ? <a href={`https://facebook.com/${encodeURIComponent(links.facebookUsername)}`} target="_blank" rel="noreferrer">Facebook <ArrowUpRight /></a> : null}
            {links?.xUsername ? <a href={`https://x.com/${encodeURIComponent(links.xUsername)}`} target="_blank" rel="noreferrer">X <ArrowUpRight /></a> : null}
            {venue.privateEventsEnabled ? <Link href="/?private-events=1#restaurants">Private events <ArrowUpRight /></Link> : null}
          </nav>
        ) : null}
      </div>
    </section>
  )
}

function groupHours(hours: NonNullable<PublicVenue['hours']>) {
  return [...hours]
    .sort((left, right) => left.dayOfWeek - right.dayOfWeek || left.startTime.localeCompare(right.startTime))
    .reduce<Array<{ day: string; windows: string[] }>>((groups, window) => {
      const day = weekdayNames[window.dayOfWeek] ?? `Day ${window.dayOfWeek}`
      const current = groups.find((item) => item.day === day)
      const label = `${capitalize(window.label)} ${formatClock(window.startTime)}–${formatClock(window.endTime)}`
      if (current) current.windows.push(label)
      else groups.push({ day, windows: [label] })
      return groups
    }, [])
}

function formatClock(value: string) {
  const [hour, minute] = value.split(':').map(Number)
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: minute ? '2-digit' : undefined,
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(2026, 0, 1, hour, minute)))
}

function formatPhone(value: string) {
  const digits = value.replaceAll(/\D/g, '')
  const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits
  return local.length === 10
    ? `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`
    : value
}

function formatParking(options: NonNullable<PublicVenue['address']>['parkingOptions']) {
  const labels = options.map((option) => ({
    valet: 'Valet parking',
    on_site: 'On-site parking',
    street: 'Street parking',
  })[option])
  return labels.join(' · ')
}

function formatLocality(city: string, region: string, postalCode: string) {
  const cityRegion = [city, region].filter(Boolean).join(', ')
  return [cityRegion, postalCode].filter(Boolean).join(' ')
}

function capitalize(value: string) {
  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`
}
